import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APICommonService } from '@nationwide/internet-servicing-angular-services';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { EnvironmentpickerService } from '../../../shared/services/environmentpicker.service';
import { LoggerService } from '../../../shared/services/logger.service';
import { TokenDataService } from '../../../shared/services/token-data.service';
import { UtilService } from '../../../shared/services/util.service';

interface PolicySummaryResponseBody {
    publicID?: string;
    policyNumber?: string;
    policyType?: string;
    status?: string;
}

export interface PolicySummaryResponse {
    body?: PolicySummaryResponseBody;
}

@Injectable({ providedIn: 'root' })
export class PolicySummaryService {
    constructor(
        private httpClient: HttpClient,
        private apiCommonService: APICommonService,
        private tokenDataService: TokenDataService,
        private util: UtilService,
        private environmentPicker: EnvironmentpickerService,
        private logger: LoggerService
    ) {
        this.logger.setModuleName('PolicySummaryService');
    }

    getPolicySummary(claimNumber: string, policyNumber?: string): Observable<PolicySummaryResponse> {
        const policySummaryUrl = `${this.environmentPicker.getEnvironmentClaimsCenterApiURL() + environment.ClaimsAPIs.claimsPolicy}claims/${claimNumber}/policy`;
        const appClientId = environment.appProps.appClientId;
        const authToken = this.tokenDataService.getToken();
        const sessionId = window.sessionStorage.getItem('sessionId');
        let headers = this.apiCommonService.buildHeader({
            appClientId,
            authToken,
            contentType: 'application/json',
            headerType: 'X-NW-Message-ID',
            sessionId: sessionId ?? undefined
        });

        const serverId = this.environmentPicker.getEnvironmentServerNumber();
        if (serverId) {
            headers = headers.append('X-NW-Server-ID-Number', serverId);
        }
        headers = headers.append('X-NW-Message-ID', this.apiCommonService.generateTransactionId());
        headers = headers.delete('x-nw-sessionid');

        const userId = this.getUserId();
        this.logger.info(`Calling policy summary API for claim "${claimNumber}" - userId: ${userId}, policyNumber: ${policyNumber || 'N/A'}`);
        this.logger.debug(`Request URL: "${policySummaryUrl}" - userId: ${userId}, policyNumber: ${policyNumber || 'N/A'}`);

        return this.httpClient.get<PolicySummaryResponse>(policySummaryUrl, { headers, observe: 'response' }).pipe(
            tap(() => this.logger.info(`Policy summary API succeeded for claim "${claimNumber}" - userId: ${userId}, policyNumber: ${policyNumber || 'N/A'}`)),
            catchError((error: HttpErrorResponse) =>
                this.util.handleError(`Error retrieving policy summary with URL: "${policySummaryUrl}"`, error))
        );
    }

    private getUserId(): string {
        return window.sessionStorage.getItem('userId') ?? '';
    }
}
